import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, ArrowLeft, Pencil, Trash2, BarChart3, Bell, Bot, Users, Sparkles } from 'lucide-react';
import { API_URL } from '../config';

const API = API_URL;

const AdminPage = ({ user, onLogout }) => {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    text: '',
    image_url: '',
    image_file: null,
    mega_folder_link: '',
    linkvertise_url: '',
    premium_key_link: '',
    post_type: 'free',
    status: 'published',
    feed_id: 'home'
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    document.title = 'EbonyThroatQueens | Admin';
    fetchPosts();
    fetchStats();
    fetchFeeds();
  }, []);

  const fetchFeeds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/feeds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeds(response.data.feeds || []);
    } catch (error) {
      console.error('Failed to load feeds:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch posts from ALL feeds by not specifying feed_id parameter
      // This requires backend to support fetching all posts when feed_id is omitted
      // Alternative: fetch from multiple feeds and combine
      const [homeResponse, eliteResponse] = await Promise.all([
        axios.get(`${API}/posts?feed_id=home&limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/posts?feed_id=eliteebony&limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      // Combine and sort by created_at
      const allPosts = [...homeResponse.data, ...eliteResponse.data];
      allPosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setPosts(allPosts);
    } catch (error) {
      toast.error('Failed to load posts');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchStats();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      let imageUrl = formData.image_url;
      
      if (formData.image_file) {
        const reader = new FileReader();
        imageUrl = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(formData.image_file);
        });
      }
      
      const postData = {
        ...formData,
        image_url: imageUrl,
        image_file: undefined
      };
      
      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.post_id}`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/posts`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post created successfully');
      }
      
      setShowCreateModal(false);
      setEditingPost(null);
      setFormData({ title: '', text: '', image_url: '', image_file: null, mega_folder_link: '', premium_key_link: '', linkvertise_url: '', post_type: 'free' });
      setImagePreview(null);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      text: post.text || '',
      image_url: post.image_url || '',
      image_file: null,
      mega_folder_link: post.mega_folder_link || '',
      linkvertise_url: post.linkvertise_url || '',
      premium_key_link: post.premium_key_link || '',
      post_type: post.post_type,
      feed_id: post.feed_id || 'home'
    });
    setImagePreview(post.image_url || null);
    setShowCreateModal(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      
      setFormData({ ...formData, image_file: file, image_url: '' });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 dark:bg-black/80 light:bg-white/90 border-b border-[#2A2A2A] dark:border-[#2A2A2A] light:border-gray-200"
        data-testid="admin-header"
      >
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Title Section */}
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Button
                onClick={() => window.location.href = '/welcome'}
                variant="ghost"
                size="sm"
                className="text-[#EE1C4E] hover:text-white hover:bg-[#1A1A1A] p-2 flex-shrink-0"
                data-testid="home-button-admin"
                title="Go to Welcome Page"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  <BarChart3 className="w-6 h-6" style={{ color: '#EE1C4E' }} />
                  Admin Dashboard
                </h1>
                <p className="text-sm text-gray-400">Manage content and users</p>
              </div>
            </div>
            
            {/* Action Buttons - Stack on mobile */}
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Button
                onClick={() => window.location.href = '/ai-agent'}
                className="h-11 px-4 text-white border-0 hover:brightness-110 flex items-center gap-2 min-w-[110px] touch-target"
                style={{ background: 'linear-gradient(135deg, #EE1C4E 0%, #FF6B9D 100%)' }}
                data-testid="ai-agent-button"
              >
                <Sparkles className="w-4 h-4" />
                <span className="text-sm">AI Agent</span>
              </Button>
              <Button
                onClick={() => window.location.href = '/telegram-setup'}
                variant="outline"
                className="h-11 px-4 text-gray-400 border-gray-700 hover:text-white flex items-center gap-2 min-w-[130px] touch-target"
                data-testid="telegram-setup-button"
              >
                <Bot className="w-4 h-4" />
                <span className="text-sm">Telegram</span>
              </Button>
              <Button
                onClick={() => window.location.href = '/mobile-post'}
                variant="outline"
                className="h-11 px-4 text-gray-400 border-gray-700 hover:text-white flex items-center gap-2 min-w-[130px] touch-target"
                data-testid="mobile-post-button"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm">Mobile Post</span>
              </Button>
            {user?.role === 'super_admin' && (
              <>
                <Button
                  onClick={() => window.location.href = '/admin/manage-admins'}
                  variant="outline"
                  className="h-11 px-4 text-gray-400 border-gray-700 hover:text-white flex items-center gap-2 min-w-[140px] touch-target"
                  data-testid="manage-admins-button"
                >
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Manage Admins</span>
                </Button>
                <Button
                  onClick={() => window.location.href = '/admin/users'}
                  variant="outline"
                  className="h-11 px-4 text-gray-400 border-gray-700 hover:text-white flex items-center gap-2 min-w-[110px] touch-target"
                  data-testid="users-list-button"
                >
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Users</span>
                </Button>
                <Button
                  onClick={() => window.location.href = '/admin/reminders'}
                  variant="outline"
                  className="h-11 px-4 text-gray-400 border-gray-700 hover:text-white flex items-center gap-2 min-w-[130px] touch-target"
                  data-testid="view-reminders-button"
                >
                  <Bell className="w-4 h-4" />
                  <span className="text-sm">Reminders</span>
                </Button>
              </>
            )}
            <Button
              onClick={() => {
                setEditingPost(null);
                setFormData({ title: '', text: '', image_url: '', image_file: null, mega_folder_link: '', premium_key_link: '', linkvertise_url: '', post_type: 'free', feed_id: 'home' });
                setImagePreview(null);
                setShowCreateModal(true);
              }}
              className="h-11 px-6 text-white font-bold rounded-lg flex items-center gap-2 w-full sm:w-auto justify-center touch-target"
              style={{ background: '#EE1C4E' }}
              data-testid="create-post-button"
            >
              <Plus className="w-5 h-5" /> Create Post
            </Button>
          </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: 'Total Users', value: stats.total_users },
              { label: 'Total Posts', value: stats.total_posts },
              { label: 'Free Posts', value: stats.free_posts },
              { label: 'Premium Posts', value: stats.premium_posts },
              { label: 'Premium Users', value: stats.premium_users }
            ].map((stat, i) => (
              <div
                key={i}
                className="p-4 sm:p-5 rounded-xl bg-[#121212] dark:bg-[#121212] light:bg-white border border-[#2A2A2A] dark:border-[#2A2A2A] light:border-gray-200"
              >
                <p className="text-gray-500 dark:text-gray-500 light:text-gray-600 text-xs mb-1">{stat.label}</p>
                <p className="text-white dark:text-white light:text-gray-900 text-2xl font-bold">{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Posts Management Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span style={{ color: '#EE1C4E' }}>📝</span> Manage Posts
            </h2>
            <span className="text-sm text-gray-500">{posts.length} total posts</span>
          </div>
          
          <div className="space-y-3 sm:space-y-4" data-testid="admin-posts-list">
            {posts.length === 0 ? (
              <div className="p-8 rounded-xl text-center bg-[#121212] dark:bg-[#121212] light:bg-white border border-[#2A2A2A] dark:border-[#2A2A2A] light:border-gray-200">
                <p className="text-gray-500 dark:text-gray-500 light:text-gray-600">No posts yet. Click "Create Post" to add one.</p>
              </div>
            ) : (
              posts.map((post) => (
                <div
                  key={post.post_id}
                  className="p-4 rounded-xl flex flex-col sm:flex-row items-start gap-4 bg-[#121212] dark:bg-[#121212] light:bg-white border border-[#2A2A2A] dark:border-[#2A2A2A] light:border-gray-200"
                  data-testid={`admin-post-${post.post_id}`}
                >
                  {/* Image Thumbnail */}
                  {post.image_url && (
                    <div className="flex-shrink-0 w-full sm:w-auto">
                      <img 
                        src={post.image_url} 
                        alt={post.title || 'Post'} 
                        className="w-full sm:w-20 h-40 sm:h-20 object-cover rounded-lg border border-[#2A2A2A] dark:border-[#2A2A2A] light:border-gray-200"
                      />
                    </div>
                  )}
                  
                  {/* Post Info */}
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-white dark:text-white light:text-gray-900 font-semibold text-base sm:text-sm truncate flex-1 min-w-[120px]">{post.title || 'Untitled'}</h3>
                      <span
                        className="px-2.5 py-1 rounded text-xs font-semibold flex-shrink-0"
                        style={{
                          background: post.post_type === 'premium' ? '#EE1C4E' : '#2A2A2A',
                          color: 'white'
                        }}
                      >
                        {post.post_type}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-500 light:text-gray-600 flex-shrink-0">
                        {post.feed_id === 'home' ? '🏠 Home' : post.feed_id === 'eliteebony' ? '👑 EliteEbony PPV' : post.feed_id}
                      </span>
                    </div>
                    <p className="text-gray-400 dark:text-gray-400 light:text-gray-600 text-sm sm:text-xs line-clamp-2 mb-2">{post.text}</p>
                    <p className="text-gray-600 dark:text-gray-600 light:text-gray-500 text-xs">
                      Created: {new Date(post.created_at).toLocaleDateString()} at {new Date(post.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                  
                  {/* Action Buttons - Stack on mobile */}
                  <div className="flex sm:flex-col gap-2 flex-shrink-0 w-full sm:w-auto justify-end">
                    <Button
                      onClick={() => handleEdit(post)}
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-white hover:bg-[#1A1A1A] h-11 sm:h-auto px-4 sm:px-2 flex-1 sm:flex-none touch-target"
                      data-testid={`edit-post-${post.post_id}`}
                      title="Edit Post"
                    >
                      <Pencil className="w-4 h-4" />
                      <span className="sm:hidden ml-2">Edit</span>
                    </Button>
                    <Button
                      onClick={() => handleDelete(post.post_id)}
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-red-500 hover:bg-[#1A1A1A] h-11 sm:h-auto px-4 sm:px-2 flex-1 sm:flex-none touch-target"
                      data-testid={`delete-post-${post.post_id}`}
                      title="Delete Post"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="sm:hidden ml-2">Delete</span>
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          style={{ background: '#121212', border: '1px solid #2A2A2A', color: 'white' }}
          data-testid="post-form-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: '#EE1C4E' }}>
              {editingPost ? 'Edit Post' : 'Create New Post'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="bg-black/40 border-gray-700 text-white"
                data-testid="post-title-input"
              />
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Feed Selection *</Label>
              <Select
                value={formData.feed_id}
                onValueChange={(value) => setFormData({ ...formData, feed_id: value })}
              >
                <SelectTrigger className="bg-black/40 border-gray-700 text-white" data-testid="feed-selector">
                  <SelectValue placeholder="Select feed" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-gray-700">
                  {feeds.map((feed) => (
                    <SelectItem 
                      key={feed.feed_id} 
                      value={feed.feed_id}
                      className="text-white hover:bg-[#2A2A2A]"
                      data-testid={`feed-option-${feed.feed_id}`}
                    >
                      {feed.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Choose which feed page this post will appear on</p>
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Content *</Label>
              <Textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                required
                rows={5}
                className="bg-black/40 border-gray-700 text-white"
                data-testid="post-text-input"
              />
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Image</Label>
              <div className="space-y-3">
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value, image_file: null })}
                  placeholder="Image URL (https://...)"
                  className="bg-black/40 border-gray-700 text-white"
                  data-testid="post-image-input"
                />
                <div className="text-center text-gray-500 text-sm">OR</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-800 file:text-gray-300 hover:file:bg-gray-700"
                  data-testid="post-image-file-input"
                />
                {imagePreview && (
                  <div className="mt-2">
                    <img src={imagePreview} alt="Preview" className="max-w-full h-32 object-cover rounded" />
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Mega Folder Link</Label>
              <Input
                value={formData.mega_folder_link}
                onChange={(e) => setFormData({ ...formData, mega_folder_link: e.target.value })}
                placeholder="https://example.com/download..."
                className="bg-black/40 border-gray-700 text-white"
                data-testid="post-mega-input"
              />
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Premium Key Link</Label>
              <Input
                value={formData.premium_key_link}
                onChange={(e) => setFormData({ ...formData, premium_key_link: e.target.value })}
                placeholder="https://example.com/key..."
                className="bg-black/40 border-gray-700 text-white"
                data-testid="post-key-input"
              />
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Linkvertise URL (for free users)</Label>
              <Input
                value={formData.linkvertise_url}
                onChange={(e) => setFormData({ ...formData, linkvertise_url: e.target.value })}
                placeholder="https://example.com/unlock..."
                className="bg-black/40 border-gray-700 text-white"
                data-testid="post-linkvertise-input"
              />
              <p className="text-xs text-gray-600 mt-1">Free users will be redirected here before accessing the key</p>
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Post Type *</Label>
              <Select value={formData.post_type} onValueChange={(value) => setFormData({ ...formData, post_type: value })}>
                <SelectTrigger className="bg-black/40 border-gray-700 text-white" data-testid="post-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}>
                  <SelectItem value="free" className="text-white">Free</SelectItem>
                  <SelectItem value="premium" className="text-white">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1 h-12 text-white font-bold rounded-full"
                style={{ background: '#EE1C4E' }}
                data-testid="save-post-button"
              >
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingPost(null);
                  setImagePreview(null);
                }}
                variant="outline"
                className="px-6 h-12 rounded-full border-gray-700 text-gray-400 hover:text-white"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;